import {
  alpha,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Fade,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  CalendarMonth as PlanIcon,
  CheckCircleOutline as RegisterIcon,
  EventRepeat as FollowUpIcon,
  ChevronRight as ChevronRightIcon,
  HelpOutline as HelpIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { useCreateCalendarEventMutation } from "../../store/ms-services/calendarApi";
import { useGetCustomerByIdQuery } from "../../store/services/customerService";
import {
  UpdateNextActionRequest,
  useUpdateNextActionMutation,
  useCompleteNextActionMutation,
} from "../../store/services/nextActionService";
import { WorkItemTypeEnum } from "../../store/services/timelineApiRTK";
import {
  getWorkItemTypeName,
  getWorkItemIcon,
  getWorkItemColor,
  isMeetingType,
  WORK_ITEM_CATEGORIES,
  WorkItemCategory,
  ACTION_RESULTS,
} from "../../utils/workItemUtils";
import MeetingBookingDialog from "../meeting-booking/MeetingBookingDialog";
import { MeetingFormData } from "../meeting-booking/types";
import WorkItemListItem from "../workitem/WorkItemListItem";
import DateSelection from "./DateSelection";
import { calculateDueDate } from "./helpers";
import { useVisualActionSelection } from "./hooks";
import { NextAction } from "./types";

type DialogMode = "select" | "plan" | "register" | "followup";

const PLAN_TYPES: WorkItemTypeEnum[] =
  WORK_ITEM_CATEGORIES[WorkItemCategory.COMMUNICATION];

// Registrera = dokumentation av en kontakt som redan skett. Använder samma
// typer som Planera (Communication) — det är inte ett kundåtagande, bara en
// logg av faktisk kontakt. Hamnar därför i Planering-spannet i dashboarden.
const REGISTER_TYPES: WorkItemTypeEnum[] =
  WORK_ITEM_CATEGORIES[WorkItemCategory.COMMUNICATION];

const FOLLOWUP_TYPES: WorkItemTypeEnum[] =
  WORK_ITEM_CATEGORIES[WorkItemCategory.FOLLOWUP];

const MODE_CONFIG = {
  plan: {
    icon: <PlanIcon />,
    title: "Planera",
    subtitle: "Påminn dig själv att kontakta kund eller planera en insats",
    color: "#1976d2",
    dialogTitle: "Planera aktivitet",
  },
  register: {
    icon: <RegisterIcon />,
    title: "Registrera",
    subtitle: "Dokumentera en kontakt som redan skett",
    color: "#2e7d32",
    dialogTitle: "Registrera aktivitet",
  },
  followup: {
    icon: <FollowUpIcon />,
    title: "Boka uppföljning",
    subtitle: "Boka en uppföljning med kunden",
    color: "#7b1fa2",
    dialogTitle: "Boka uppföljning",
  },
} as const;

interface VisualActionDialogProps {
  open: boolean;
  customerNo: number;
  onClose: () => void;
  onNextActionChange?: (nextAction: NextAction) => void;
  customerName?: string;
  workId?: number;
}

const VisualActionDialog: React.FC<VisualActionDialogProps> = ({
  open,
  customerNo,
  onClose,
  onNextActionChange,
  customerName,
  workId,
}) => {
  const [mode, setMode] = useState<DialogMode>("select");
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [justRegistered, setJustRegistered] = useState<string | null>(null);

  const {
    selectedAction,
    dateOption,
    customDate,
    description,
    setDescription,
    handleActionClick,
    handleDateOptionChange,
    handleCustomDateChange,
    handleSlotSelect,
    resetSelections,
  } = useVisualActionSelection();

  const [customTitle, setCustomTitle] = useState("");
  const [registerComment, setRegisterComment] = useState("");

  const [updateNextAction, { isLoading: isUpdating }] = useUpdateNextActionMutation();
  const [completeNextAction, { isLoading: isCompleting }] = useCompleteNextActionMutation();
  const [createMeeting, { isLoading: isCreatingMeeting }] = useCreateCalendarEventMutation();
  const { data: customer } = useGetCustomerByIdQuery(customerNo, { skip: !open });

  const isLoading = isUpdating || isCompleting;

  const predefinedTitles: Record<string, string[]> = {
    [String(WorkItemTypeEnum.PhoneCall)]: ["Kundsamtal", "Statusuppdatering", "Informationssamtal"],
    [String(WorkItemTypeEnum.Email)]: ["E-post till kund", "Offert via e-post", "Informationsutskick"],
    [String(WorkItemTypeEnum.PhysicalMeeting)]: ["Kundmöte", "Platsbesök", "Produktdemo"],
    [String(WorkItemTypeEnum.DigitalMeeting)]: ["Videomöte", "Teams-möte", "Digital presentation"],
    [String(WorkItemTypeEnum.Sms)]: ["SMS till kund", "Påminnelse via SMS"],
    [String(WorkItemTypeEnum.FollowUpPhoneCall)]: ["Uppföljningssamtal", "Kontrollsamtal", "Statusuppdatering"],
    [String(WorkItemTypeEnum.FollowUpEmail)]: ["Uppföljning via e-post", "Bekräftelse och nästa steg"],
    [String(WorkItemTypeEnum.FollowUpPhysicalMeeting)]: ["Personligt möte", "Kundbesök på plats"],
    [String(WorkItemTypeEnum.FollowUpDigitalMeeting)]: ["Videomöte", "Digital presentation"],
  };

  const currentTitles = selectedAction ? predefinedTitles[selectedAction] || [] : [];

  useEffect(() => {
    if (selectedAction && !customTitle) {
      setCustomTitle(getWorkItemTypeName(Number(selectedAction) as WorkItemTypeEnum));
    }
  }, [selectedAction, customTitle]);

  // Only open meeting dialog for meeting types in followup mode
  const shouldOpenMeetingDialog = (targetMode: DialogMode, type: WorkItemTypeEnum) =>
    targetMode === "followup" && isMeetingType(type);

  // Quick-select: jump straight to a mode with type pre-selected
  const handleQuickSelect = (targetMode: "plan" | "register" | "followup", type: WorkItemTypeEnum) => {
    setMode(targetMode);
    handleActionClick(type.toString());
    if (shouldOpenMeetingDialog(targetMode, type)) {
      setShowMeetingDialog(true);
    } else {
      setCustomTitle("");
    }
  };

  const handleClose = () => {
    resetSelections();
    setCustomTitle("");
    setMode("select");
    setRegisterComment("");
    setJustRegistered(null);
    setShowMeetingDialog(false);
    onClose();
  };

  const handleTypeSelect = (type: WorkItemTypeEnum) => {
    handleActionClick(type.toString());
    if (shouldOpenMeetingDialog(mode, type)) {
      setShowMeetingDialog(true);
    } else {
      setCustomTitle("");
    }
  };

  const handleBackToModes = () => {
    resetSelections();
    setCustomTitle("");
    setRegisterComment("");
    setJustRegistered(null);
    setMode("select");
  };

  const handleBackToTypes = () => {
    resetSelections();
    setCustomTitle("");
    setRegisterComment("");
  };

  const handlePlanSubmit = async () => {
    if (!selectedAction) return;
    try {
      const dueDate = calculateDueDate(dateOption, customDate, selectedAction);
      const workItemType = Number(selectedAction) as WorkItemTypeEnum;
      const finalTitle = customTitle || getWorkItemTypeName(workItemType);

      const result = await updateNextAction({
        customerNo,
        request: {
          title: finalTitle,
          description: description || `${finalTitle} för kund`,
          scheduledDate: dueDate,
          workNo: workId,
          type: workItemType,
        },
      }).unwrap();

      if (onNextActionChange) onNextActionChange(result);
      handleClose();
    } catch (err) {
      console.error("Error creating planned activity:", err);
    }
  };

  const handleRegisterSubmit = async (planNext: boolean) => {
    if (!selectedAction) return;
    try {
      const workItemType = Number(selectedAction) as WorkItemTypeEnum;
      const finalTitle = customTitle || getWorkItemTypeName(workItemType);
      const now = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");

      const result = await updateNextAction({
        customerNo,
        request: {
          title: finalTitle,
          description: registerComment || `${finalTitle} för kund`,
          scheduledDate: now,
          workNo: workId,
          type: workItemType,
        },
      }).unwrap();

      if (result.WorkItemNo) {
        await completeNextAction({
          customerNo,
          res: {
            WorkItemNo: result.WorkItemNo,
            WorkItemResult: {
              Result: ACTION_RESULTS.COMPLETED,
              Comment: registerComment,
            },
          },
        }).unwrap();
      }

      if (onNextActionChange) onNextActionChange(result);

      if (planNext) {
        // Reset form and go back to top level
        resetSelections();
        setCustomTitle("");
            setRegisterComment("");
        setJustRegistered(finalTitle);
        setMode("select");
      } else {
        handleClose();
      }
    } catch (err) {
      console.error("Error registering activity:", err);
    }
  };

  const handleMeetingSubmit = async (meetingData: MeetingFormData) => {
    try {
      const workItemType = Number(selectedAction) as WorkItemTypeEnum;
      const formatISOString = (date: Date) => date.toLocaleString("sv-SE");

      const request: UpdateNextActionRequest = {
        title: meetingData.subject,
        description: meetingData.description,
        scheduledDate: formatISOString(meetingData.startDateTime),
        workNo: workId,
        type: workItemType,
      };

      const actionResult = await updateNextAction({ customerNo, request }).unwrap();

      const meetingResult = await createMeeting({
        subject: meetingData.subject,
        body: { content: meetingData.description, contentType: "HTML" },
        start: { dateTime: meetingData.startDateTime.toISOString(), timeZone: "utc" },
        end: { dateTime: meetingData.endDateTime.toISOString(), timeZone: "utc" },
        isOnlineMeeting: selectedAction === String(WorkItemTypeEnum.FollowUpDigitalMeeting),
        location: meetingData.location ? { displayName: meetingData.location } : undefined,
        transactionId: actionResult.WorkItemNo?.toString(),
        attendees: meetingData.attendees.map((a) => ({
          emailAddress: { name: a.name ?? "", address: a.email },
          type: "required",
        })),
      }).unwrap();

      const updatedResult = await updateNextAction({
        customerNo,
        request: { ...request, workItemNo: actionResult.WorkItemNo, externalId: meetingResult.id },
      }).unwrap();

      if (onNextActionChange) onNextActionChange(updatedResult);
      handleClose();
    } catch (err) {
      console.error("Error creating meeting:", err);
      throw err;
    }
  };

  const isValidAction = selectedAction !== null && !isNaN(Number(selectedAction));
  const selectedType = isValidAction ? (Number(selectedAction) as WorkItemTypeEnum) : null;
  const showPlanForm = isValidAction && (mode === "plan" || (mode === "followup" && !isMeetingType(selectedType)));
  const showRegisterForm = isValidAction && mode === "register";

  const getTypeLabel = (type: WorkItemTypeEnum) => getWorkItemTypeName(type);

  const getTypesForMode = (): WorkItemTypeEnum[] => {
    switch (mode) {
      case "plan": return PLAN_TYPES;
      case "register": return REGISTER_TYPES;
      case "followup": return FOLLOWUP_TYPES;
      default: return [];
    }
  };

  const modeColor = mode !== "select" ? MODE_CONFIG[mode].color : undefined;

  return (
    <>
      <Dialog
        open={open && !showMeetingDialog}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: "hidden" },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: modeColor ? alpha(modeColor, 0.06) : "transparent",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {mode !== "select" && (
            <IconButton
              size="small"
              onClick={isValidAction ? handleBackToTypes : handleBackToModes}
              sx={{ mr: 0.5 }}
            >
              <BackIcon fontSize="small" />
            </IconButton>
          )}
          {mode !== "select" && (
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: alpha(modeColor!, 0.12),
                color: modeColor,
              }}
            >
              {MODE_CONFIG[mode].icon}
            </Avatar>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {mode === "select" ? "Ny aktivitet" : MODE_CONFIG[mode].dialogTitle}
              </Typography>
              {mode === "select" && (
                <Tooltip
                  arrow
                  placement="bottom-start"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "background.paper",
                        color: "text.primary",
                        boxShadow: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        maxWidth: 340,
                        p: 1.75,
                      },
                    },
                    arrow: { sx: { color: "background.paper" } },
                  }}
                  title={
                    <Box>
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}
                      >
                        Vilket alternativ ska jag välja?
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          lineHeight: 1.5,
                          color: "text.secondary",
                          mb: 1,
                        }}
                      >
                        <strong>Planera</strong> — en påminnelse till dig själv
                        att kontakta kunden senare. Intern, kunden vet inte om
                        det än.
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          lineHeight: 1.5,
                          color: "text.secondary",
                          mb: 1,
                        }}
                      >
                        <strong>Registrera</strong> — dokumentation av en
                        kontakt som <em>redan har skett</em>. T.ex. kunden
                        ringde dig oannonserat, eller du svarade på ett mail.
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          lineHeight: 1.5,
                          color: "text.secondary",
                        }}
                      >
                        <strong>Boka uppföljning</strong> — du och kunden har
                        avtalat ett möte eller samtal vid en bestämd tid. Det
                        är ett kundåtagande.
                      </Typography>
                    </Box>
                  }
                >
                  <IconButton
                    size="small"
                    sx={{ color: "text.secondary", p: 0.25 }}
                    aria-label="Hjälp"
                  >
                    <HelpIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {mode === "select" && (
              <Typography variant="body2" color="text.secondary">
                {customer?.Name || customerName || "Välj vad du vill göra"}
              </Typography>
            )}
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {justRegistered && (
            <Box
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: alpha("#2e7d32", 0.08),
                borderBottom: "1px solid",
                borderColor: alpha("#2e7d32", 0.2),
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <RegisterIcon sx={{ color: "success.main", fontSize: 20 }} />
              <Typography variant="body2" color="success.dark">
                <strong>{justRegistered}</strong> registrerad — planera nästa steg
              </Typography>
            </Box>
          )}

          {/* Step 1: Mode selection */}
          {mode === "select" && (
            <Fade in>
              <List disablePadding>
                {(Object.keys(MODE_CONFIG) as Array<keyof typeof MODE_CONFIG>).map(
                  (key, index) => {
                    const cfg = MODE_CONFIG[key];
                    // Shortcuts: Phone & Email mapped to each mode's type set
                    const shortcuts = key === "plan"
                      ? [
                          { type: WorkItemTypeEnum.PhoneCall, label: "Samtal" },
                          { type: WorkItemTypeEnum.Email, label: "E-post" },
                        ]
                      : [
                          { type: WorkItemTypeEnum.FollowUpPhoneCall, label: "Samtal" },
                          { type: WorkItemTypeEnum.FollowUpEmail, label: "E-post" },
                        ];

                    return (
                      <React.Fragment key={key}>
                        {index > 0 && <Divider />}
                        <ListItemButton
                          onClick={() => setMode(key)}
                          sx={{
                            px: 3,
                            py: 2,
                            "&:hover": { bgcolor: alpha(cfg.color, 0.04) },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: alpha(cfg.color, 0.1),
                                color: cfg.color,
                                width: 48,
                                height: 48,
                              }}
                            >
                              {cfg.icon}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight={600}>
                                {cfg.title}
                              </Typography>
                            }
                            secondary={cfg.subtitle}
                            sx={{ ml: 1 }}
                          />
                          <ChevronRightIcon sx={{ color: "text.disabled" }} />
                        </ListItemButton>

                        {/* Quick-select shortcuts under each mode */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            px: 3,
                            pb: 2,
                            pl: 11.5, // align with text (avatar + spacing)
                          }}
                        >
                          {shortcuts.map(({ type, label }) => {
                            const color = getWorkItemColor(type);
                            return (
                              <Chip
                                key={type}
                                avatar={
                                  <Avatar sx={{ bgcolor: `${alpha(color, 0.15)} !important`, color: `${color} !important` }}>
                                    {getWorkItemIcon(type)}
                                  </Avatar>
                                }
                                label={label}
                                clickable
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuickSelect(key, type);
                                }}
                                variant="outlined"
                                sx={{
                                  borderColor: alpha(color, 0.25),
                                  "&:hover": {
                                    bgcolor: alpha(color, 0.08),
                                    borderColor: alpha(color, 0.4),
                                  },
                                }}
                              />
                            );
                          })}
                          <Chip
                            label="Fler..."
                            clickable
                            onClick={(e) => {
                              e.stopPropagation();
                              setMode(key);
                            }}
                            variant="outlined"
                            sx={{
                              borderColor: "divider",
                              color: "text.secondary",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          />
                        </Box>
                      </React.Fragment>
                    );
                  }
                )}
              </List>
            </Fade>
          )}

          {/* Step 2: Type selection */}
          {mode !== "select" && !isValidAction && (
            <Fade in>
              <List disablePadding>
                {getTypesForMode().map((type, index) => {
                  const color = getWorkItemColor(type);
                  return (
                    <React.Fragment key={type}>
                      {index > 0 && <Divider variant="inset" />}
                      <ListItemButton
                        onClick={() => handleTypeSelect(type)}
                        sx={{
                          px: 3,
                          py: 1.5,
                          "&:hover": { bgcolor: alpha(color, 0.06) },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: alpha(color, 0.1),
                              color: color,
                              width: 40,
                              height: 40,
                            }}
                          >
                            {getWorkItemIcon(type)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={getTypeLabel(type)}
                          sx={{ ml: 0.5 }}
                        />
                        <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                      </ListItemButton>
                    </React.Fragment>
                  );
                })}
              </List>
            </Fade>
          )}

          {/* Plan / followup (non-meeting): form */}
          {showPlanForm && (
            <Fade in>
              <Stack spacing={2.5} sx={{ p: 3 }}>
                <SelectedTypeBanner
                  type={Number(selectedAction) as WorkItemTypeEnum}
                  label={getTypeLabel(Number(selectedAction) as WorkItemTypeEnum)}
                  onClear={handleBackToTypes}
                />
                <TextField
                  label="Titel"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  fullWidth
                  size="small"
                />
                {currentTitles.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {currentTitles.map((title) => (
                      <Chip
                        key={title}
                        label={title}
                        clickable
                        variant={customTitle === title ? "filled" : "outlined"}
                        color={customTitle === title ? "primary" : "default"}
                        onClick={() => setCustomTitle(title)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
                <TextField
                  label="Beskrivning"
                  value={description ?? ""}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                />
                <DateSelection
                  dateOption={dateOption}
                  customDate={customDate}
                  isUpdating={isUpdating}
                  selectedAction={selectedAction}
                  onDateOptionChange={handleDateOptionChange}
                  onCustomDateChange={handleCustomDateChange}
                  onSlotSelect={handleSlotSelect}
                />
              </Stack>
            </Fade>
          )}

          {/* Register mode: form */}
          {showRegisterForm && (
            <Fade in>
              <Stack spacing={2.5} sx={{ p: 3 }}>
                <SelectedTypeBanner
                  type={Number(selectedAction) as WorkItemTypeEnum}
                  label={getTypeLabel(Number(selectedAction) as WorkItemTypeEnum)}
                  onClear={handleBackToTypes}
                />
                <TextField
                  label="Titel"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  fullWidth
                  size="small"
                />
                {currentTitles.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {currentTitles.map((title) => (
                      <Chip
                        key={title}
                        label={title}
                        clickable
                        variant={customTitle === title ? "filled" : "outlined"}
                        color={customTitle === title ? "primary" : "default"}
                        onClick={() => setCustomTitle(title)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
                <TextField
                  label="Kommentar"
                  value={registerComment}
                  onChange={(e) => setRegisterComment(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Kort sammanfattning av vad som hände..."
                />
              </Stack>
            </Fade>
          )}
        </DialogContent>

        {/* Plan / followup (non-meeting): preview + actions */}
        {showPlanForm && (
          <>
            <Divider />
            <WorkItemListItem
              workItemType={Number(selectedAction) as WorkItemTypeEnum}
              title={customTitle || getWorkItemTypeName(Number(selectedAction) as WorkItemTypeEnum)}
              description={description || "Ingen beskrivning"}
              date={calculateDueDate(dateOption, customDate, selectedAction)}
              customerName={customer?.Name || customerName || "Okänd kund"}
              onAction={handleBackToTypes}
            />
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleClose} disabled={isUpdating}>
                Avbryt
              </Button>
              <Button
                variant="contained"
                onClick={handlePlanSubmit}
                disabled={isUpdating || (dateOption === "custom" && !customDate)}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {isUpdating ? <CircularProgress size={20} /> : "Schemalägg"}
              </Button>
            </DialogActions>
          </>
        )}

        {/* Register mode: actions */}
        {showRegisterForm && (
          <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Button onClick={handleClose} disabled={isLoading}>
              Avbryt
            </Button>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Registrera & stäng">
              <IconButton
                onClick={() => handleRegisterSubmit(false)}
                disabled={isLoading}
                size="small"
                sx={{
                  color: "success.main",
                  border: "1px solid",
                  borderColor: "success.main",
                  "&:hover": { bgcolor: alpha("#2e7d32", 0.08) },
                }}
              >
                {isLoading ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="success"
              onClick={() => handleRegisterSubmit(true)}
              disabled={isLoading}
              endIcon={!isLoading && <ChevronRightIcon />}
              sx={{ borderRadius: 2, px: 3, textTransform: "none" }}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : "Registrera & nästa"}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Meeting Booking Dialog for follow-up mode */}
      <MeetingBookingDialog
        open={showMeetingDialog}
        onClose={() => {
          setShowMeetingDialog(false);
          resetSelections();
        }}
        onSubmit={handleMeetingSubmit}
        workItemType={
          selectedAction ? (Number(selectedAction) as WorkItemTypeEnum) : undefined
        }
        customerNo={customerNo}
        customerName={customerName ?? ""}
        isLoading={isCreatingMeeting}
      />
    </>
  );
};

/** Banner showing selected activity type with option to change */
const SelectedTypeBanner: React.FC<{
  type: WorkItemTypeEnum;
  label?: string;
  onClear: () => void;
}> = ({ type, label, onClear }) => {
  const color = getWorkItemColor(type);
  return (
    <ButtonBase
      onClick={onClear}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        bgcolor: alpha(color, 0.06),
        border: "1px solid",
        borderColor: alpha(color, 0.2),
        width: "100%",
        justifyContent: "flex-start",
        textAlign: "left",
        "&:hover": {
          bgcolor: alpha(color, 0.1),
        },
      }}
    >
      <Avatar sx={{ bgcolor: alpha(color, 0.15), color, width: 36, height: 36 }}>
        {getWorkItemIcon(type)}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ color, fontWeight: 600 }}>
          {label ?? getWorkItemTypeName(type)}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Byt
      </Typography>
    </ButtonBase>
  );
};

export default VisualActionDialog;
